import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { exportApi, downloadGuestExport } from "@/lib/api";
import { FieldPicker } from "./FieldPicker";
import { TemplateBar } from "./TemplateBar";

const STATUSES = [["all", "All guests"], ["attending", "Attending"], ["declined", "Not attending"], ["pending", "Pending"]];

export function CustomizeExportDialog({ open, onOpenChange, eventId, defaultStatus = "all" }) {
  const [meta, setMeta] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(defaultStatus);
  const [format, setFormat] = useState("xlsx");
  const [busy, setBusy] = useState("");

  const loadTemplates = useCallback(() => exportApi.templates().then(setTemplates).catch(() => {}), []);

  useEffect(() => {
    if (!open) return;
    setStatus(defaultStatus);
    exportApi.fields(eventId).then((m) => { setMeta(m); setSelected(new Set(m.fields.map((f) => f.key))); }).catch(() => toast.error("Could not load fields"));
    loadTemplates();
  }, [open, eventId, defaultStatus, loadTemplates]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (meta?.fields || []).filter((f) => !q || f.label.toLowerCase().includes(q) || f.group.toLowerCase().includes(q));
  }, [meta, search]);

  const toggle = (k) => setSelected((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const setMany = (items, on) => setSelected((s) => { const n = new Set(s); items.forEach((f) => (on ? n.add(f.key) : n.delete(f.key))); return n; });

  const run = async (all) => {
    if (!all && !selected.size) { toast.error("Select at least one field"); return; }
    setBusy(all ? "all" : "selected");
    try {
      const keys = all ? null : meta.fields.map((f) => f.key).filter((k) => selected.has(k));
      const rows = await downloadGuestExport(eventId, { format, keys, status });
      toast.success(`Exported ${rows} guest record${rows === "1" ? "" : "s"}`);
    } catch {
      toast.error("Export failed");
    } finally {
      setBusy("");
    }
  };

  const applyTemplate = (t) => {
    const keys = new Set((meta?.fields || []).map((f) => f.key));
    setSelected(new Set(t.keys.filter((k) => keys.has(k))));
    if (t.status) setStatus(t.status);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col gap-0 overflow-hidden p-0" data-testid="customize-export-dialog">
        <DialogHeader className="border-b border-border p-6 pb-4">
          <DialogTitle className="font-serif text-2xl">Customize export</DialogTitle>
          <DialogDescription>
            Pick the RSVP and Guest Form fields to include.{meta && ` ${meta.fields.length} fields available · ${meta.guest_count} RSVP records · ${meta.form_submission_count} form submissions.`}
          </DialogDescription>
          {meta && <div className="pt-3"><TemplateBar presets={meta.presets} templates={templates} onApply={applyTemplate} onSaved={loadTemplates} selected={selected} status={status} /></div>}
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 border-b border-border px-6 py-3">
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search fields…" data-testid="export-field-search" className="w-full rounded-xl border border-border bg-background py-2 pl-9 pr-3 text-sm" />
          </div>
          <button onClick={() => setMany(visible, true)} data-testid="export-select-all" className="rounded-xl border border-border px-3 py-2 text-xs font-semibold transition-colors hover:bg-secondary">Select all</button>
          <button onClick={() => setMany(visible, false)} data-testid="export-deselect-all" className="rounded-xl border border-border px-3 py-2 text-xs font-semibold transition-colors hover:bg-secondary">Deselect all</button>
          <span className="text-xs font-semibold text-muted-foreground" data-testid="export-selected-count">{selected.size} selected</span>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {meta ? <FieldPicker fields={visible} selected={selected} toggle={toggle} toggleGroup={setMany} /> : <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>}
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-border bg-secondary/30 px-6 py-4">
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="export-status-select" className="rounded-xl border border-border bg-background px-3 py-2 text-sm font-medium">
            {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <div className="flex rounded-xl border border-border bg-background p-0.5" data-testid="export-format-toggle">
            {[["xlsx", "Excel", FileSpreadsheet], ["csv", "CSV", FileText]].map(([v, l, Icon]) => (
              <button key={v} onClick={() => setFormat(v)} data-testid={`export-format-${v}`} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${format === v ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
                <Icon className="h-4 w-4" /> {l}
              </button>
            ))}
          </div>
          <div className="ml-auto flex gap-2">
            <button onClick={() => run(true)} disabled={!!busy || !meta} data-testid="export-all-fields-btn" className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-secondary disabled:opacity-60">
              {busy === "all" && <Loader2 className="h-4 w-4 animate-spin" />} Export all fields
            </button>
            <button onClick={() => run(false)} disabled={!!busy || !meta} data-testid="export-selected-fields-btn" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60">
              {busy === "selected" && <Loader2 className="h-4 w-4 animate-spin" />} Export selected ({selected.size})
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
